import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { AgentRunEvent, Brief, TaskDetail } from "@/types";
import { api } from "@/services/api";
import { webAgentTransport } from "@/services/agent-transport";

export const useBriefStore = defineStore("briefs", () => {
  const briefs = ref<Brief[]>([]);
  const active = ref<Brief | null>(null);
  const activeTask = ref<TaskDetail | null>(null);
  const loading = ref(false);
  const agentProgress = ref("");
  const streamedReply = ref("");
  const agentThinking = ref("");
  const agentSources = ref<Array<{ title: string; url: string; snippet?: string }>>([]);
  const agentSteps = ref<Array<{ key: string; label: string; status: "active" | "done" }>>([]);
  const currentRunId = ref<number | null>(null);
  const agentTraceComplete = ref(false);
  let seenEventIds = new Set<number>();
  let replaceStreamedReplyOnNextDelta = false;
  const submitted = computed(() =>
    briefs.value.filter((brief) => brief.status !== "draft"),
  );

  async function loadList() {
    briefs.value = (await api<{ briefs: Brief[] }>("/briefs")).briefs;
  }
  async function open(id: number) {
    active.value = (await api<{ brief: Brief }>(`/briefs/${id}`)).brief;
  }
  async function create(title = "未命名 Brief") {
    active.value = (
      await api<{ brief: Brief }>("/briefs", {
        method: "POST",
        body: JSON.stringify({ title }),
      })
    ).brief;
    await loadList();
  }
  async function sendRequirement(content: string, assetIds: number[] = []) {
    if (!active.value) return;
    const brief = active.value;
    const temporaryMessageId = -Date.now();
    brief.messages.push({
      id: temporaryMessageId,
      role: "user",
      content,
      created_at: new Date().toISOString(),
    });
    loading.value = true;
    agentProgress.value = "正在发送需求";
    streamedReply.value = "";
    agentThinking.value = "";
    agentSources.value = [];
    agentSteps.value = [];
    agentTraceComplete.value = false;
    seenEventIds = new Set<number>();
    replaceStreamedReplyOnNextDelta = false;
    try {
      const run = await webAgentTransport.start(brief.id, { content, assetIds });
      currentRunId.value = run.id;
      let terminalError = "";
      await webAgentTransport.follow(run.id, (event) => {
        terminalError ||= applyAgentEvent(event);
      });
      if (terminalError) throw new Error(terminalError);
      await open(brief.id);
      await loadList();
    } catch (error) {
      await open(brief.id).catch(() => {
        if (active.value?.id === brief.id)
          active.value.messages = active.value.messages.filter(
            (message) => message.id !== temporaryMessageId,
          );
      });
      throw error;
    } finally {
      loading.value = false;
      currentRunId.value = null;
      agentTraceComplete.value = true;
      streamedReply.value = "";
    }
  }
  function applyAgentEvent(event: AgentRunEvent) {
    if (seenEventIds.has(event.id)) return "";
    seenEventIds.add(event.id);
    if (event.type === "attempt_reset") {
      replaceStreamedReplyOnNextDelta = Boolean(streamedReply.value);
      agentThinking.value = "";
      agentSources.value = [];
      agentSteps.value = [];
      agentProgress.value = String(event.payload.label || "正在后台重新尝试");
    }
    if (event.type === "progress") {
      const key = String(event.payload.key || "working");
      const label = String(event.payload.label || "正在处理");
      agentProgress.value = label;
      agentSteps.value = agentSteps.value.map((step) => ({ ...step, status: "done" }));
      const existing = agentSteps.value.find((step) => step.key === key);
      if (existing) {
        existing.label = label;
        existing.status = "active";
      } else {
        agentSteps.value.push({ key, label, status: "active" });
      }
    }
    if (event.type === "thinking_delta") {
      agentThinking.value += String(event.payload.text || "");
    }
    if (event.type === "answer_delta") {
      if (replaceStreamedReplyOnNextDelta) {
        streamedReply.value = "";
        replaceStreamedReplyOnNextDelta = false;
      }
      streamedReply.value += String(event.payload.text || "");
    }
    if (event.type === "sources") {
      const incoming = Array.isArray(event.payload.sources)
        ? event.payload.sources
        : [];
      agentSources.value = [
        ...new Map(
          [...agentSources.value, ...incoming]
            .filter((source) => /^https?:\/\//.test(String(source?.url || "")))
            .map((source) => [source.url, source]),
        ).values(),
      ];
    }
    if (event.type === "complete") {
      agentProgress.value = "方案已完成";
      agentSteps.value = agentSteps.value.map((step) => ({ ...step, status: "done" }));
      agentTraceComplete.value = true;
    }
    if (event.type === "error") return String(event.payload.message || "Agent 执行失败");
    if (event.type === "cancelled") return "任务已取消";
    return "";
  }
  async function resumeAgentRun() {
    if (!active.value || loading.value) return;
    const run = await webAgentTransport.active(active.value.id);
    if (!run) return;
    currentRunId.value = run.id;
    loading.value = true;
    agentTraceComplete.value = false;
    agentThinking.value = "";
    streamedReply.value = "";
    agentSources.value = [];
    agentSteps.value = [];
    seenEventIds = new Set<number>();
    replaceStreamedReplyOnNextDelta = false;
    let terminalError = "";
    try {
      await webAgentTransport.follow(run.id, (event) => {
        terminalError ||= applyAgentEvent(event);
      });
      await open(run.brief_id);
      await loadList();
      if (terminalError) throw new Error(terminalError);
    } finally {
      loading.value = false;
      currentRunId.value = null;
      agentTraceComplete.value = true;
      streamedReply.value = "";
    }
  }
  async function cancelAgentRun() {
    if (!currentRunId.value) return;
    await webAgentTransport.cancel(currentRunId.value);
  }
  async function renameActive(title: string) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/title`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      })
    ).brief;
    await loadList();
  }
  async function deleteBrief(id: number) {
    await api(`/briefs/${id}`, { method: "DELETE" });
    briefs.value = briefs.value.filter((brief) => brief.id !== id);
    if (active.value?.id === id) active.value = null;
  }
  async function confirmTask(taskId: number, confirmed: boolean) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(
        `/briefs/${active.value.id}/tasks/${taskId}/confirm`,
        { method: "POST", body: JSON.stringify({ confirmed }) },
      )
    ).brief;
  }
  async function submit() {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/submit`, {
        method: "POST",
      })
    ).brief;
    await loadList();
  }
  async function createChange(content: string) {
    if (!active.value) return;
    active.value = (
      await api<{ brief: Brief }>(`/briefs/${active.value.id}/changes`, {
        method: "POST",
        body: JSON.stringify({ content }),
      })
    ).brief;
  }
  async function openTask(id: number) {
    activeTask.value = (await api<{ task: TaskDetail }>(`/tasks/${id}`)).task;
  }
  async function refresh() {
    if (active.value) await open(active.value.id);
  }
  return {
    briefs,
    active,
    activeTask,
    loading,
    agentProgress,
    streamedReply,
    agentThinking,
    agentSources,
    agentSteps,
    currentRunId,
    agentTraceComplete,
    submitted,
    loadList,
    open,
    create,
    sendRequirement,
    resumeAgentRun,
    cancelAgentRun,
    renameActive,
    deleteBrief,
    confirmTask,
    submit,
    createChange,
    openTask,
    refresh,
  };
});
